import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Wrench,
    AlertTriangle,
    Clock,
    Bell,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Eye,
    Package,
    Truck,
    Hammer,
    Monitor,
    Smartphone,
    Anchor,
    Armchair,
    Zap,
    CalendarClock,
    ShieldAlert,
    RefreshCw,
    AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../components/ui/utils';
import api from '../../utils/api';
import { toast } from 'sonner';
import { toSydneyDate } from '../../utils/helpers';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ActionItemsData {
    repairApprovals: RepairApprovalItem[];
    damagedAssets: DamagedAssetItem[];
    overdueMaintenance: OverdueMaintenanceItem[];
    upcomingReminders: UpcomingReminderItem[];
}

interface RepairApprovalItem {
    id: number;
    description: string;
    vendor: string | null;
    cost: number;
    repairDate: string;
    status: string;
    asset: { id: number; name: string; tag: string; type: string; image?: string };
    approvedBy: { id: number; name: string } | null;
    createdBy: { id: number; name: string } | null;
}

interface DamagedAssetItem {
    id: number;
    name: string;
    tag: string;
    type: string;
    status: string;
    image?: string;
    assignedToUser?: { id: number; name: string } | null;
    assignedToDepartment?: { id: number; name: string } | null;
}

interface OverdueMaintenanceItem {
    id: number;
    type: string;
    scheduledDate: string;
    status: string;
    description: string | null;
    asset: { id: number; name: string; tag: string; type: string };
}

interface UpcomingReminderItem {
    id: number;
    eventName: string;
    eventType: string;
    nextDueDate: string;
    recurrencePattern: string;
    status: string;
    asset: { id: number; name: string; tag: string; type: string };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getTypeIcon = (type: string, size = 16) => {
    switch (type) {
        case 'Vehicle': return <Truck size={size} />;
        case 'Plant & Equipment': return <Wrench size={size} />;
        case 'Tools': return <Hammer size={size} />;
        case 'IT Equipment': return <Monitor size={size} />;
        case 'Mobile Devices': return <Smartphone size={size} />;
        case 'Lifting/Slings': return <Anchor size={size} />;
        case 'Furniture': return <Armchair size={size} />;
        default: return <Package size={size} />;
    }
};

const formatDate = (date: string) => toSydneyDate(date);

const daysUntil = (date: string) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.floor((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

const daysOverdue = (date: string) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
};

// ─── Section Wrapper ──────────────────────────────────────────────────────────

interface SectionProps {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    count: number;
    accentColor: string;
    badgeColor: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
}

const Section: React.FC<SectionProps> = ({
    icon, title, subtitle, count, accentColor, badgeColor, children, defaultOpen = true
}) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            <button
                className="w-full flex items-center justify-between p-5 md:p-6 hover:bg-gray-50/60 transition-colors"
                onClick={() => setOpen(o => !o)}
            >
                <div className="flex items-center gap-3">
                    <div className={cn('p-2.5 rounded-2xl', accentColor)}>
                        {icon}
                    </div>
                    <div className="text-left">
                        <h2 className="font-bold text-gray-900 text-base leading-tight">{title}</h2>
                        <p className="text-gray-400 text-xs font-medium mt-0.5">{subtitle}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <span className={cn(
                        'text-xs font-black px-2.5 py-1 rounded-full',
                        count > 0 ? badgeColor : 'bg-gray-100 text-gray-400'
                    )}>
                        {count}
                    </span>
                    {open
                        ? <ChevronUp size={18} className="text-gray-400" />
                        : <ChevronDown size={18} className="text-gray-400" />
                    }
                </div>
            </button>

            {open && (
                <div className="border-t border-gray-100 px-5 md:px-6 pb-5 md:pb-6 pt-5">
                    {count === 0 ? (
                        <div className="py-8 flex flex-col items-center text-center">
                            <CheckCircle2 size={36} className="text-green-400 mb-2" />
                            <p className="text-sm font-bold text-gray-500">All clear!</p>
                            <p className="text-xs text-gray-400 mt-0.5">No items in this category</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 xl:gap-5">
                            {children}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonCard = () => (
    <div className="animate-pulse bg-gray-50 rounded-2xl p-4 space-y-3">
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-200 rounded-xl shrink-0" />
            <div className="flex-1 space-y-2">
                <div className="h-3.5 bg-gray-200 rounded-lg w-2/3" />
                <div className="h-3 bg-gray-100 rounded-lg w-1/3" />
            </div>
        </div>
        <div className="h-9 bg-gray-100 rounded-xl w-full" />
    </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const AssetActionItems: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<ActionItemsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [approvingId, setApprovingId] = useState<number | null>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await api.get<ActionItemsData>('/api/assets/action-items');
            setData(res);
        } catch (err) {
            console.error('Failed to load action items:', err);
            toast.error('Failed to load action items');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleApprove = async (assetId: number, repairId: number) => {
        try {
            setApprovingId(repairId);
            await api.patch(`/api/assets/${assetId}/repair/${repairId}/approve`, {});
            toast.success('Repair approved successfully');
            fetchData();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to approve repair');
        } finally {
            setApprovingId(null);
        }
    };

    const totalCount = data
        ? data.repairApprovals.length +
          data.damagedAssets.length +
          data.overdueMaintenance.length +
          data.upcomingReminders.length
        : 0;

    return (
        <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0 max-w-[1600px] mx-auto w-full">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50 shrink-0"
                    onClick={() => navigate('/assets')}
                >
                    <ArrowLeft size={20} />
                </Button>
                <div className="min-w-0">
                    <div className="flex items-center gap-2.5">
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Action Items</h1>
                        {!loading && totalCount > 0 && (
                            <span className="bg-orange-100 text-orange-700 text-xs font-black px-2.5 py-1 rounded-full">
                                {totalCount}
                            </span>
                        )}
                    </div>
                    <p className="text-gray-500 text-sm font-medium mt-0.5">
                        Tasks and alerts that require your attention
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="icon"
                    className="ml-auto rounded-xl border-gray-200 bg-white hover:bg-gray-50 shrink-0"
                    onClick={fetchData}
                    disabled={loading}
                >
                    <RefreshCw size={16} className={loading ? 'animate-spin text-gray-400' : 'text-gray-600'} />
                </Button>
            </div>

            {/* All clear banner */}
            {!loading && data && totalCount === 0 && (
                <div className="bg-green-50 border border-green-100 rounded-3xl p-10 flex flex-col items-center text-center gap-3">
                    <div className="p-4 bg-green-100 rounded-full">
                        <CheckCircle2 size={40} className="text-green-500" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-green-800">You're all caught up!</h2>
                        <p className="text-green-600 text-sm font-medium mt-1">
                            No outstanding action items at this time.
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        className="mt-2 border-green-200 text-green-700 hover:bg-green-100 font-bold rounded-xl"
                        onClick={() => navigate('/assets')}
                    >
                        Back to Assets
                    </Button>
                </div>
            )}

            {/* Loading skeletons */}
            {loading && (
                <div className="space-y-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 space-y-3">
                            <div className="flex items-center gap-3 animate-pulse">
                                <div className="w-10 h-10 bg-gray-100 rounded-2xl" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 bg-gray-100 rounded-lg w-1/3" />
                                    <div className="h-3 bg-gray-50 rounded-lg w-1/4" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 xl:gap-5 pt-3">
                                <SkeletonCard />
                                <SkeletonCard />
                                <SkeletonCard />
                                <SkeletonCard />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Section 1: Repair Record Approvals ─────────────────────── */}
            {!loading && data && (
                <>
                    <Section
                        icon={<ShieldAlert size={18} className="text-purple-600" />}
                        title="Repair Record Approvals"
                        subtitle="Repair records awaiting your approval"
                        count={data.repairApprovals.length}
                        accentColor="bg-purple-50"
                        badgeColor="bg-purple-100 text-purple-700"
                        defaultOpen
                    >
                        {data.repairApprovals.map(repair => (
                            <div
                                key={repair.id}
                                className="bg-gray-50/70 border border-gray-100 rounded-2xl p-4 flex flex-col h-full gap-3 hover:border-purple-100 transition-colors"
                            >
                                {/* Asset info */}
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-500 shrink-0">
                                        {getTypeIcon(repair.asset.type)}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-bold text-gray-900 text-sm truncate">{repair.asset.name}</p>
                                        <p className="text-[10px] font-black text-gray-400 tracking-wider uppercase">{repair.asset.tag}</p>
                                    </div>
                                    <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 shrink-0">
                                        Pending Approval
                                    </Badge>
                                </div>

                                {/* Repair Details */}
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                                    <div className="bg-white rounded-xl px-3 py-2 border border-gray-100">
                                        <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px] block mb-0.5">Date</span>
                                        <span className="font-bold text-gray-700">{formatDate(repair.repairDate)}</span>
                                    </div>
                                    <div className="bg-white rounded-xl px-3 py-2 border border-gray-100">
                                        <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px] block mb-0.5">Cost</span>
                                        <span className="font-bold text-gray-700">${Number(repair.cost || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="bg-white rounded-xl px-3 py-2 border border-gray-100 col-span-2 md:col-span-1">
                                        <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px] block mb-0.5">Submitted By</span>
                                        <span className="font-bold text-gray-700 truncate block">{repair.createdBy?.name || '—'}</span>
                                    </div>
                                </div>

                                {repair.description && (
                                    <p className="text-xs text-gray-500 font-medium bg-white border border-gray-100 rounded-xl px-3 py-2 line-clamp-2">
                                        {repair.description}
                                    </p>
                                )}

                                {/* Actions */}
                                <div className="flex items-center gap-2 mt-auto pt-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 rounded-xl border-gray-200 text-gray-700 font-bold text-xs h-9 gap-1.5"
                                        onClick={() => navigate(`/assets/${repair.asset.id}`)}
                                    >
                                        <Eye size={14} />
                                        View Asset
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 rounded-xl border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 font-bold text-xs h-9 gap-1.5"
                                        onClick={() => navigate(`/assets/${repair.asset.id}/repair/${repair.id}`)}
                                    >
                                        <Wrench size={14} />
                                        View Repair
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="flex-1 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold text-xs h-9 gap-1.5 shadow-sm shadow-green-200"
                                        onClick={() => handleApprove(repair.asset.id, repair.id)}
                                        disabled={approvingId === repair.id}
                                    >
                                        <CheckCircle2 size={14} />
                                        {approvingId === repair.id ? 'Approving…' : 'Approve'}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </Section>

                    {/* ── Section 2: Damaged / Needs Repair Assets ──────────── */}
                    <Section
                        icon={<AlertTriangle size={18} className="text-red-500" />}
                        title="Damaged or Needs Repair"
                        subtitle="Assets flagged as damaged or requiring repair"
                        count={data.damagedAssets.length}
                        accentColor="bg-red-50"
                        badgeColor="bg-red-100 text-red-700"
                        defaultOpen
                    >
                        {data.damagedAssets.map(asset => (
                            <div
                                key={asset.id}
                                className="bg-gray-50/70 border border-gray-100 rounded-2xl p-4 flex flex-col h-full gap-3 hover:border-red-100 transition-colors"
                            >
                                <div className="flex items-start gap-3">
                                    <div className={cn(
                                        'w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border',
                                        asset.status === 'Damaged'
                                            ? 'bg-red-50 border-red-100 text-red-500'
                                            : 'bg-orange-50 border-orange-100 text-orange-500'
                                    )}>
                                        {getTypeIcon(asset.type)}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-bold text-gray-900 text-sm truncate">{asset.name}</p>
                                        <p className="text-[10px] font-black text-gray-400 tracking-wider uppercase">{asset.tag}</p>
                                    </div>
                                    <Badge className={cn(
                                        'border-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 shrink-0',
                                        asset.status === 'Damaged'
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-orange-100 text-orange-700'
                                    )}>
                                        {asset.status}
                                    </Badge>
                                </div>

                                {(asset.assignedToUser || asset.assignedToDepartment) && (
                                    <div className="bg-white rounded-xl px-3 py-2 border border-gray-100 text-xs">
                                        <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px] block mb-0.5">Assigned To</span>
                                        <span className="font-bold text-gray-700">
                                            {asset.assignedToUser?.name || asset.assignedToDepartment?.name || '—'}
                                        </span>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="flex items-center gap-2 mt-auto pt-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 rounded-xl border-gray-200 text-gray-700 font-bold text-xs h-9 gap-1.5"
                                        onClick={() => navigate(`/assets/${asset.id}`)}
                                    >
                                        <Eye size={14} />
                                        View Asset
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs h-9 gap-1.5 shadow-sm shadow-blue-200"
                                        onClick={() => navigate(`/assets/${asset.id}/repair/new`)}
                                    >
                                        <Wrench size={14} />
                                        Record Repair
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </Section>

                    {/* ── Section 3: Overdue Maintenance ───────────────────── */}
                    <Section
                        icon={<CalendarClock size={18} className="text-amber-600" />}
                        title="Overdue Maintenance"
                        subtitle="Scheduled maintenance that has passed its due date"
                        count={data.overdueMaintenance.length}
                        accentColor="bg-amber-50"
                        badgeColor="bg-amber-100 text-amber-700"
                        defaultOpen
                    >
                        {data.overdueMaintenance.map(record => {
                            const overdue = daysOverdue(record.scheduledDate);
                            return (
                                <div
                                    key={record.id}
                                    className="bg-gray-50/70 border border-gray-100 rounded-2xl p-4 flex flex-col h-full gap-3 hover:border-amber-100 transition-colors"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-500 shrink-0">
                                            {getTypeIcon(record.asset.type)}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-bold text-gray-900 text-sm truncate">{record.asset.name}</p>
                                            <p className="text-[10px] font-black text-gray-400 tracking-wider uppercase">{record.asset.tag}</p>
                                        </div>
                                        <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 shrink-0">
                                            {record.status}
                                        </Badge>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                                        <div className="bg-white rounded-xl px-3 py-2 border border-gray-100">
                                            <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px] block mb-0.5">Type</span>
                                            <span className="font-bold text-gray-700">{record.type}</span>
                                        </div>
                                        <div className="bg-white rounded-xl px-3 py-2 border border-gray-100">
                                            <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px] block mb-0.5">Scheduled</span>
                                            <span className="font-bold text-gray-700">{formatDate(record.scheduledDate)}</span>
                                        </div>
                                        <div className={cn(
                                            'rounded-xl px-3 py-2 border col-span-2 md:col-span-1',
                                            overdue > 30
                                                ? 'bg-red-50 border-red-100'
                                                : overdue > 7
                                                    ? 'bg-orange-50 border-orange-100'
                                                    : 'bg-amber-50 border-amber-100'
                                        )}>
                                            <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px] block mb-0.5">Overdue By</span>
                                            <div className="flex items-center gap-1">
                                                <Clock size={12} className={overdue > 30 ? 'text-red-500' : 'text-amber-500'} />
                                                <span className={cn(
                                                    'font-bold',
                                                    overdue > 30 ? 'text-red-700' : overdue > 7 ? 'text-orange-700' : 'text-amber-700'
                                                )}>
                                                    {overdue} day{overdue !== 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {record.description && (
                                        <p className="text-xs text-gray-500 font-medium bg-white border border-gray-100 rounded-xl px-3 py-2 line-clamp-2">
                                            {record.description}
                                        </p>
                                    )}

                                    <div className="flex items-center gap-2 mt-auto pt-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex-1 rounded-xl border-gray-200 text-gray-700 font-bold text-xs h-9 gap-1.5"
                                            onClick={() => navigate(`/assets/${record.asset.id}`)}
                                        >
                                            <Eye size={14} />
                                            View Asset
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="flex-1 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs h-9 gap-1.5 shadow-sm shadow-amber-200"
                                            onClick={() => navigate(`/assets/${record.asset.id}/maintenance/${record.id}`)}
                                        >
                                            <CalendarClock size={14} />
                                            View Maintenance
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </Section>

                    {/* ── Section 4: Upcoming / Overdue Reminders ───────────── */}
                    <Section
                        icon={<Bell size={18} className="text-blue-500" />}
                        title="Upcoming & Overdue Reminders"
                        subtitle="Active reminders due within the next 30 days"
                        count={data.upcomingReminders.length}
                        accentColor="bg-blue-50"
                        badgeColor="bg-blue-100 text-blue-700"
                        defaultOpen
                    >
                        {data.upcomingReminders.map(reminder => {
                            const days = daysUntil(reminder.nextDueDate);
                            const isOverdue = days < 0;
                            const isDueSoon = days >= 0 && days <= 7;

                            return (
                                <div
                                    key={reminder.id}
                                    className="bg-gray-50/70 border border-gray-100 rounded-2xl p-4 flex flex-col h-full gap-3 hover:border-blue-100 transition-colors"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={cn(
                                            'w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border',
                                            isOverdue
                                                ? 'bg-red-50 border-red-100 text-red-500'
                                                : isDueSoon
                                                    ? 'bg-orange-50 border-orange-100 text-orange-500'
                                                    : 'bg-blue-50 border-blue-100 text-blue-500'
                                        )}>
                                            {isOverdue ? <AlertCircle size={16} /> : <Bell size={16} />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-bold text-gray-900 text-sm truncate">{reminder.eventName}</p>
                                            <p className="text-[10px] font-black text-gray-400 tracking-wider uppercase">
                                                {reminder.asset.name} · {reminder.asset.tag}
                                            </p>
                                        </div>
                                        <Badge className={cn(
                                            'border-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 shrink-0',
                                            isOverdue
                                                ? 'bg-red-100 text-red-700'
                                                : isDueSoon
                                                    ? 'bg-orange-100 text-orange-700'
                                                    : 'bg-blue-100 text-blue-700'
                                        )}>
                                            {isOverdue ? 'Overdue' : `${days}d left`}
                                        </Badge>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                                        <div className="bg-white rounded-xl px-3 py-2 border border-gray-100">
                                            <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px] block mb-0.5">Type</span>
                                            <span className="font-bold text-gray-700">{reminder.eventType}</span>
                                        </div>
                                        <div className="bg-white rounded-xl px-3 py-2 border border-gray-100">
                                            <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px] block mb-0.5">Due Date</span>
                                            <span className="font-bold text-gray-700">{formatDate(reminder.nextDueDate)}</span>
                                        </div>
                                        <div className="bg-white rounded-xl px-3 py-2 border border-gray-100 col-span-2 md:col-span-1">
                                            <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px] block mb-0.5">Recurrence</span>
                                            <span className="font-bold text-gray-700">{reminder.recurrencePattern}</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 mt-auto pt-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex-1 rounded-xl border-gray-200 text-gray-700 font-bold text-xs h-9 gap-1.5"
                                            onClick={() => navigate(`/assets/${reminder.asset.id}`)}
                                        >
                                            <Eye size={14} />
                                            View Asset
                                        </Button>
                                        <Button
                                            size="sm"
                                            className={cn(
                                                'flex-1 rounded-xl text-white font-bold text-xs h-9 gap-1.5 shadow-sm',
                                                isOverdue
                                                    ? 'bg-red-600 hover:bg-red-700 shadow-red-200'
                                                    : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'
                                            )}
                                            onClick={() => navigate(`/assets/${reminder.asset.id}?tab=reminders`)}
                                        >
                                            <Bell size={14} />
                                            View Reminder
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </Section>
                </>
            )}
        </div>
    );
};

export default AssetActionItems;
